/**
 * Public reference data fetched once at boot.
 *
 * Migrated to the Sayagie contract. Every path now carries its mandatory
 * trailing slash and comes from the central endpoints map. Each fetcher
 * returns a plain array, so callers no longer have to know which of the four
 * response envelopes a given endpoint uses.
 *
 * Verified against dev on 2026-09-16: the four taxonomy endpoints and
 * /currencies/ all answer with their own named envelope
 * ({ containerTypes: [...] } and so on), while countries moved under
 * /api/geography/ and answers with a Spring page.
 */

import CLIENT from "../lib/api/client";
import ENDPOINTS from "../lib/api/endpoints";

/**
 * Unwraps whichever envelope the endpoint used: a named key, a Spring page's
 * `content`, or a bare array.
 */
const pickList = (payload, key) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.[key])) return payload[key];
  if (Array.isArray(payload?.content)) return payload.content;

  return [];
};

const fetchList = async (url, key, params) => {
  const { data } = await CLIENT.get(url, params ? { params } : undefined);
  return pickList(data, key);
};

export const fetchContainerTypes = () =>
  fetchList(ENDPOINTS.TAXONOMY.containerTypes, "containerTypes");

export const fetchFuelTypes = () =>
  fetchList(ENDPOINTS.TAXONOMY.fuelTypes, "fuelTypes");

export const fetchContainerOptions = () =>
  fetchList(ENDPOINTS.TAXONOMY.containerOptions, "containerOptions");

export const fetchContainerMaterials = () =>
  fetchList(ENDPOINTS.TAXONOMY.containerMaterials, "containerMaterials");

/**
 * Geography is the one domain under `/api/`, and it paginates 1-based with
 * `page_size` rather than Sapphire's 0-based `size`. `page_size=-1` returns
 * the full set, which is what the country picker needs.
 */
export const fetchAllCountries = () =>
  fetchList(ENDPOINTS.GEOGRAPHY.countries, "countries", {
    page: 1,
    page_size: -1,
  });

export const fetchAllCurrencies = () => fetchList(ENDPOINTS.CURRENCIES.root, "currencies");
